/**
 * Which facility a route belongs to, for deciding what a navigation may cancel.
 * Facility routes are the ones whose matched records carry `meta.facilityScope` (the same test the
 * router's facility access guard uses); their `:id` is the facility id.
 */

/**
 * @param {{ params?: Object, matched?: Array<{ meta?: Object }> }|null} route
 * @returns {string|null} Lower-cased facility id, or null outside a facility.
 */
export function facilityIdOf(route) {
  if (!route) return null;
  const isFacilityRoute = (route.matched || []).some((record) => record.meta && record.meta.facilityScope);
  const id = route.params && route.params.id;
  return isFacilityRoute && id ? String(id).toLowerCase() : null;
}

/**
 * True when a navigation leaves the facility whose data is loading: another facility, or no facility.
 * @param {Object} from - Route being left
 * @param {Object} to - Route being entered
 * @returns {boolean}
 */
export function facilityChangedBetween(from, to) {
  return facilityIdOf(from) !== facilityIdOf(to);
}

/**
 * True when a navigation leaves the page: another route, or the same route with other params.
 * A query- or hash-only change (a dashboard's ?tab=, a list's filters) stays on the page, so the
 * page's own requests must survive it: the page started them for the state the URL now records.
 * @param {Object|null} from - Route being left
 * @param {Object} to - Route being entered
 * @returns {boolean}
 */
export function pageChangedBetween(from, to) {
  if (!from || !from.name || from.name !== to.name) return true;
  const fromParams = from.params || {};
  const toParams = to.params || {};
  const keys = new Set([...Object.keys(fromParams), ...Object.keys(toParams)]);
  return [...keys].some((key) => String(fromParams[key] ?? '') !== String(toParams[key] ?? ''));
}
